import { z } from 'zod';
import { ERROR_MESSAGES } from '~/constants';
import { AppActorSystem } from '~/features/xstateSystem';
import { MedicalLicense, MedicalLicenseType } from '../../../types';
import { optionSchema, optionSchemaRequired } from '../../types';
import { NON_PRACTISING_STATUSES } from './constants';
import { Events } from './events';

// [MCV-927] Backend column for inactive explanation is VARCHAR(4096).
// Keep this constant in lockstep with the schema `.max` and the form's char counter.
export const INACTIVE_EXPLAIN_MAX = 4096;

/**
 * [MCV-1562] Today in the user's local calendar, formatted as `YYYY-MM-DD`.
 * Date-only form values are calendar days rather than instants, and ISO dates
 * sort lexicographically, so comparing the strings avoids UTC boundary shifts.
 */
const todayAsCalendarDay = (): string => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${today.getFullYear()}-${month}-${day}`;
};

/**
 * [MCV-1562] The dropdown supplies canonical casing, but imported rows can
 * carry variants such as `EXPIRED` or `expired, revoked` and still load into
 * this form, so matching is normalized here.
 */
const isExpiredLabel = (label: string): boolean => label.trim().toLowerCase().startsWith('expired');

/**
 * [MCV-1562] Normalize Active for the same imported-data case as Expired so
 * both existing form rules apply consistently to non-canonical casing.
 */
const isActiveLabel = (label: string): boolean => label.trim().toLowerCase() === 'active';

const NORMALIZED_NON_PRACTISING_STATUSES = new Set(
  NON_PRACTISING_STATUSES.map((status) => status.toLowerCase())
);

/**
 * [MCV-1574] Whether currStatus represents a licence that is not currently
 * practising. Drives whether inactiveDate/inactiveExplainTx render on the
 * Dates step and are required, replacing the old manual "Is the License
 * Inactive?" toggle which was independent of the selected status. Normalized
 * like isExpiredLabel/isActiveLabel above so imported rows with
 * non-canonical casing still resolve correctly.
 */
export const isNonPractisingLabel = (label: string): boolean =>
  NORMALIZED_NON_PRACTISING_STATUSES.has(label.trim().toLowerCase());

/**
 * [MCV-1718] Whether a non-practising licence must carry this inactive field.
 *
 * The one grandfather rule behind both the schema refinements below and the
 * form's required markers, so the asterisk can never claim more than
 * validation enforces. A row that loaded with this same status and the field
 * already blank may keep it blank; anything else must fill it in.
 */
export const isInactiveFieldRequired = (
  statusLabel: string,
  originalStatusLabel: string | undefined,
  originalValue: string | undefined
): boolean => {
  const statusUnchanged = originalStatusLabel !== undefined && originalStatusLabel === statusLabel;
  const wasAlreadyMissing = originalValue !== undefined && !originalValue;
  return !statusUnchanged || !wasAlreadyMissing;
};

export const medicalLicenseSchema = z
  .object({
    licenseType: optionSchemaRequired,
    licenseNumber: z.string().min(1, ERROR_MESSAGES.REQUIRED),
    origIssueDate: z.iso.date(ERROR_MESSAGES.INVALID_DATE),
    renewalDate: z.iso.date(ERROR_MESSAGES.INVALID_DATE),
    expiryDate: z
      .string()
      .min(1, ERROR_MESSAGES.REQUIRED)
      .pipe(z.iso.date(ERROR_MESSAGES.INVALID_DATE)),
    currStatus: optionSchemaRequired,
    // [MCV-1135] Tighten to ISO date (matching other date fields) so malformed
    // input like "12/12/54545" is rejected with a clear INVALID_DATE error
    // inline instead of slipping through to the submission path and surfacing
    // as a raw "Cannot read properties of undefined (reading 'toString')" toast.
    inactiveDate: z
      .union([z.iso.date(ERROR_MESSAGES.INVALID_DATE), z.literal(''), z.null()])
      .optional(),
    inactiveExplainTx: z
      .string()
      .max(INACTIVE_EXPLAIN_MAX, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(INACTIVE_EXPLAIN_MAX))
      .optional()
      .nullable(),
    stateCode: optionSchema.optional(),
    countryCode: optionSchema,
    id: z.number().nullish(),
    validFlag: z.boolean(),
    warnings: z.record(z.string(), z.string()).optional(),
    // Form-only snapshots let cross-field rules distinguish a conflict the
    // user creates from one already present on an imported row. Dropped by
    // formDataToMedicalLicense like the other form-only fields.
    originalCurrStatus: z.string().optional(),
    originalExpiryDate: z.string().optional(),
    originalInactiveDate: z.string().optional(),
    // [MCV-1574] Same grandfathering purpose as the snapshots above, for the
    // newly-required inactiveExplainTx.
    originalInactiveExplainTx: z.string().optional(),
  })
  .refine(
    (data) => {
      return new Date(data.origIssueDate) <= new Date();
    },
    {
      message: ERROR_MESSAGES.FUTURE_DATE,
      path: ['origIssueDate'],
    }
  )
  .refine(
    (data) =>
      !data.renewalDate ||
      !data.origIssueDate ||
      new Date(data.origIssueDate) < new Date(data.renewalDate),
    {
      message: ERROR_MESSAGES.INVALID_DATE_ORDER('Issue Date', 'Renewal Date'),
      path: ['renewalDate'],
    }
  )
  .refine(
    (data) =>
      !data.expiryDate ||
      !data.origIssueDate ||
      new Date(data.origIssueDate) < new Date(data.expiryDate),
    {
      message: ERROR_MESSAGES.INVALID_DATE_ORDER('Issue Date', 'Expiration Date'),
      path: ['expiryDate'],
    }
  )
  .refine(
    (data) =>
      !data.inactiveDate ||
      !data.origIssueDate ||
      new Date(data.origIssueDate) < new Date(data.inactiveDate),
    {
      message: ERROR_MESSAGES.INVALID_DATE_ORDER('Issue Date', 'Inactive Date'),
      path: ['inactiveDate'],
    }
  )
  .refine(
    (data) =>
      !data.inactiveDate ||
      !data.renewalDate ||
      new Date(data.renewalDate) < new Date(data.inactiveDate),
    {
      message: ERROR_MESSAGES.INVALID_DATE_ORDER('Renewal Date', 'Inactive Date'),
      path: ['inactiveDate'],
    }
  )
  .refine(
    (data) =>
      !data.inactiveDate ||
      !data.expiryDate ||
      new Date(data.expiryDate) < new Date(data.inactiveDate),
    {
      message: ERROR_MESSAGES.INVALID_DATE_ORDER('Expiration Date', 'Inactive Date'),
      path: ['inactiveDate'],
    }
  )
  .refine(
    (data) => !isActiveLabel(data.currStatus.label) || data.expiryDate > todayAsCalendarDay(),
    {
      message: 'Expiry date cannot be in the past if license is active.',
      path: ['expiryDate'],
    }
  )
  // [MCV-1562] Mirror image of the rule above. Active requires a day after
  // today; an Expired status allows today or earlier, so the boundary belongs
  // to exactly one state and the rules neither overlap nor leave a gap.
  //
  // Scoped by the "Expired" prefix, which covers all seven expired variants
  // ('Expired, Revoked' and friends) without catching Suspended, Probation,
  // Revoked, Retired or the other restriction states -- those sit on licences
  // whose expiry is legitimately still ahead. Deliberately NOT written as
  // `label !== 'Active'`, which would silently impose this on all 18
  // non-Active statuses.
  .refine(
    (data) => {
      if (
        !isExpiredLabel(data.currStatus.label) ||
        !data.expiryDate ||
        data.expiryDate <= todayAsCalendarDay()
      ) {
        return true;
      }

      const statusUnchanged =
        data.originalCurrStatus !== undefined && data.originalCurrStatus === data.currStatus.label;
      const expiryUnchanged =
        data.originalExpiryDate !== undefined && data.originalExpiryDate === data.expiryDate;
      return statusUnchanged && expiryUnchanged;
    },
    {
      message: 'Expiry date cannot be in the future if license is expired.',
      path: ['expiryDate'],
    }
  )
  // [MCV-594] Grandfathers a row that arrived in the bad state.
  //
  // The pre-MCV-594 client allowed Active + an inactive date, so such rows are
  // already stored. [MCV-1574] Visibility of inactiveDate is now derived from
  // currStatus rather than the removed manual toggle, and the clear-on-leave
  // effect in MedicalLicenseForm wipes inactiveDate the moment status leaves
  // the non-practising set, so a FRESH Active + inactiveDate pair can no
  // longer be introduced through the real UI. This rule stays as defense in
  // depth for rows that still carry the legacy combination on load, keyed
  // directly on the presence of a date now that there is no separate flag.
  //
  // It only fires when the user is actually touching the pair. "Unchanged"
  // is judged the same way the API judges it, so client and server agree on
  // which saves are allowed rather than the UI passing something the endpoint
  // then rejects with no field to attach the message to.
  .refine(
    (data) => {
      if (!isActiveLabel(data.currStatus.label) || !data.inactiveDate) return true;
      const statusUnchanged =
        data.originalCurrStatus !== undefined && data.originalCurrStatus === data.currStatus.label;
      const dateUnchanged =
        data.originalInactiveDate !== undefined &&
        (data.originalInactiveDate || '') === (data.inactiveDate || '');
      return statusUnchanged && dateUnchanged;
    },
    {
      message: 'An active license cannot be marked inactive.',
      path: ['inactiveDate'],
    }
  )
  // [MCV-1574] inactiveDate/inactiveExplainTx are required once currStatus
  // is non-practising, replacing the old inactiveCheck-gated requirement.
  //
  // [MCV-1561]-style grandfather: `inactive_date`/`inactive_explain_tx` were
  // added nullable with no backfill, so real rows exist with a
  // non-practising status and a NULL date/explanation. A blind required
  // check would freeze every PATCH to one of those rows (including edits
  // that touch neither field). Require the field only when the user is the
  // one introducing the non-practising status + missing value -- i.e. NOT
  // when that exact combination already existed on the loaded row.
  //
  // Guarded on isNonPractisingLabel so this can never raise an error against
  // a field the Dates step has hidden (mirrors the warning in
  // board-certification/schemas.ts about refining conditionally-rendered
  // fields).
  .refine(
    (data) => {
      if (!isNonPractisingLabel(data.currStatus.label) || data.inactiveDate) return true;

      return !isInactiveFieldRequired(
        data.currStatus.label,
        data.originalCurrStatus,
        data.originalInactiveDate
      );
    },
    {
      message: 'Inactive date is required if license is inactive.',
      path: ['inactiveDate'],
    }
  )
  .refine(
    (data) => {
      if (!isNonPractisingLabel(data.currStatus.label) || data.inactiveExplainTx) return true;

      return !isInactiveFieldRequired(
        data.currStatus.label,
        data.originalCurrStatus,
        data.originalInactiveExplainTx
      );
    },
    {
      message: 'Explanation is required if license is inactive.',
      path: ['inactiveExplainTx'],
    }
  )
  // [MCV-921] Schema must match the UI: US licenses require a state.
  .refine((data) => data.countryCode?.value !== 'US' || !!data.stateCode?.value, {
    message: ERROR_MESSAGES.REQUIRED,
    path: ['stateCode'],
  });

export type MedLicenseFormValues = z.infer<typeof medicalLicenseSchema>;
export type CreateMedLicenseFormValues = Omit<MedLicenseFormValues, 'id'>;
export type MedicalLicenseSortOptions =
  | 'license_type'
  | 'license_number'
  | 'location'
  | 'curr_status'
  | 'expiry_date'
  | 'id';

export type MedicalTypeOptions = z.infer<typeof optionSchema>;

export type MedicalLicenseSort = {
  sortBy: MedicalLicenseSortOptions;
  license_type: number;
  license_number: number;
  location: number;
  curr_status: number;
  expiry_date: number;
  id: number;
  page: number;
  pageSize: number;
};

export interface FilterSelect {
  type: string;
  label: string;
  options: { label: string; value: string }[];
  cyTag: string;
}

export interface MedicalLicenseFilter {
  status: string;
  location: string;
  expiration: string;
}

export interface MedicalTypeSchema {
  value: number;
  label: string;
}
export interface MedicalLicenseContext {
  loading: boolean;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
  medicalLicenses: MedLicenseFormValues[];
  medLicensesForm: MedLicenseFormValues | CreateMedLicenseFormValues;
  modal: {
    edit: boolean;
    create: boolean;
  };
  sort: MedicalLicenseSort;
  filter: MedicalLicenseFilter;
  idToDelete: number;
  _medicalLicenseTypeOptions: MedicalTypeOptions[];
}

export interface MedicalLicenseResponse {
  data: MedicalLicense;
}

export interface MedLicenseTypeResponse {
  data: MedicalLicenseType[];
}

export type MedicalLicensesResponse = MedLicenseFormValues[];

export interface Scope {
  context: MedicalLicenseContext;
  event: Events;
  system: AppActorSystem;
}
